import type { Issue, Me } from '@ft/shared';
import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { api } from '../lib/http';
import { getAreaId, isAreaMember, switchArea, canSeePath } from '../lib/area';
import { applyTheme, isDark, readTheme, type Theme } from '../theme';
import { Icon } from './icons';
import { CommandPalette, ShortcutHelp, useShortcuts } from './CommandPalette';
import { Tour, tourDone } from './Tour';
import { FreshnessChip } from './Freshness';
import { ProfilePhotoDialog } from './ProfilePhoto';
import { useToast } from './ui';
import { Avatar } from '../lib/people';

export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const { t, lang, setLang } = useI18n();
  const [theme, setTheme] = useState<Theme>(readTheme);
  useEffect(() => applyTheme(theme), [theme]);
  const dark = isDark(theme);

  // Anzahl dringender/zu prüfender Punkte als Zähler in der Leiste; bei jedem Seitenwechsel aktualisiert
  const loc = useLocation();
  const [urgent, setUrgent] = useState(0);
  useEffect(() => { if (!me.areas.length || !me.sections.includes('issues')) return; api<Issue[]>('GET', '/api/issues').then((l) => setUrgent(l.filter((i) => i.level !== 'info' && !i.snoozedUntil).length), () => undefined); }, [loc.pathname, me.areas.length]);

  const [menu, setMenu] = useState(false);
  const [photoToast, sayPhoto] = useToast();
  const [overlay, setOverlay] = useState<'search' | 'keys' | 'tour' | 'photo' | null>(() => (me.areas.length && !tourDone() ? 'tour' : null));
  const open = useCallback((w: 'search' | 'keys') => setOverlay(w), []);
  useShortcuts(open);
  useEffect(() => { const h = () => setOverlay('tour'); window.addEventListener('ft:tour', h); return () => window.removeEventListener('ft:tour', h); }, []);
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  // Gruppiert: Start und Offene Punkte oben, dann Verträge & Bestellungen, Abrechnung, Steuerung
  const NAV: { to: string; label: string; icon: ReactNode; end?: boolean }[] = [
    { to: '/', label: t('nav.start'), icon: Icon.home, end: true },
    { to: '/offene-punkte', label: t('nav.issues'), icon: Icon.issues },
    { to: '/vertraege', label: t('nav.contracts'), icon: Icon.contracts },
    { to: '/bestellungen', label: t('nav.orders'), icon: Icon.orders },
    { to: '/invoicing', label: t('nav.invoicing'), icon: Icon.invoicing },
    { to: '/projekte', label: t('nav.projects'), icon: Icon.projects },
    { to: '/forecast', label: t('nav.forecast'), icon: Icon.forecast },
    ...(me.role !== 'reader' ? [{ to: '/marge', label: t('nav.margin'), icon: Icon.margin }] : []),
    ...(me.role !== 'reader' || me.deliveryHead ? [{ to: '/report', label: t('nav.report'), icon: Icon.report }] : []),
  ].filter((n) => canSeePath(me, n.to));
  // Gruppenüberschrift beim ersten sichtbaren Eintrag einer Gruppe
  const groupAt = new Map<number, string>();
  { let cur = ''; const order = ['/vertraege', '/bestellungen', '/invoicing', '/projekte', '/forecast', '/marge', '/report'];
    const groupOf = (to: string) => (['/vertraege', '/bestellungen'].includes(to) ? 'nav.group.orders' : to === '/invoicing' ? 'nav.group.billing' : order.includes(to) ? 'nav.group.steering' : '');
    NAV.forEach((n, i) => { const g = groupOf(n.to); if (g && g !== cur) groupAt.set(i, t(g as 'nav.group.orders')); cur = g; }); }
  const cls = ({ isActive }: { isActive: boolean }) => `railitem${isActive ? ' active' : ''}`;

  return (
    <div className={`shell${me.env !== 'prod' ? ' nonprod' : ''}`}>
      {me.env !== 'prod' && <div className="envbar" role="status">{t(me.env === 'test' ? 'env.test' : 'env.dev')}</div>}
      <header className="appbar">
        <Link to="/" className="logobox" aria-label={t('app.toStart')}>intive</Link>
        <span className="appname">{t('app.name')}</span>
        <span className="area">
          <span>{t('app.area')}</span>
          {me.areas.length > 1
            ? <select className="areasel" aria-label={t('app.areaSwitch')} value={getAreaId() ?? ''} onChange={(e) => switchArea(e.target.value)}>{me.areas.map((a) => <option key={a.id} value={a.id}>{a.member ? a.name : t('area.viewOnlyOpt', { name: a.name })}</option>)}</select>
            : <b>{me.areas[0]?.name ?? '–'}</b>}
          {me.areas.length > 0 && !isAreaMember() && <span className="pill info" title={t('area.viewOnlyHelp')}>{t('area.viewOnly')}</span>}
          {me.role === 'finance' && isAreaMember() && <span className="pill info" title={t('shell.finance')}>{t('role.finance')}</span>}
        </span>
        <span className="spacer" />
        {me.areas.length > 0 && (
          <button className="searchbtn" onClick={() => setOverlay('search')} aria-label={t('search.title')}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <span>{t('search.button')}</span><kbd>{mac ? '⌘' : t('keys.ctrl')} K</kbd>
          </button>
        )}
        <FreshnessChip me={me} />
        <div className="seg langseg" role="group" aria-label={t('app.lang')}>
          <button aria-pressed={lang === 'de'} onClick={() => setLang('de')} lang="de" title="Deutsch">DE</button>
          <button aria-pressed={lang === 'en'} onClick={() => setLang('en')} lang="en" title="English">EN</button>
        </div>
        <button className="iconbtn" onClick={() => setTheme(dark ? 'light' : 'dark')} aria-label={dark ? t('app.toLight') : t('app.toDark')} title={t('app.theme')}>
          {dark ? Icon.sun : Icon.moon}
        </button>
        <div className="memenu">
          <button className="mebtn" aria-haspopup="menu" aria-expanded={menu} aria-label={t('me.menu')} title={`${me.name} (${me.email})`} onClick={() => setMenu((v) => !v)}>
            <Avatar id={me.id} name={me.name} initials={me.initials} size={34} kind="avatar" title={`${me.name} (${me.email})`} />
          </button>
          {menu && <>
            <div className="mebackdrop" onClick={() => setMenu(false)} />
            <div className="medrop" role="menu" onKeyDown={(e) => { if (e.key === 'Escape') setMenu(false); }}>
              <div className="mehd"><Avatar id={me.id} name={me.name} initials={me.initials} size={40} kind="avatar" /><div><b>{me.name}</b><span>{t(`role.${me.role}`)}{me.deliveryHead ? ' · Delivery Head' : ''}</span><span>{me.email}</span></div></div>
              <button role="menuitem" autoFocus onClick={() => { setMenu(false); setOverlay('photo'); }}>{t('photo.menu')}</button>
              <a role="menuitem" href="/auth/logout">{t('app.logout')}</a>
            </div>
          </>}
        </div>
      </header>
      <div className="frame">
        <nav className="rail" aria-label={t('app.modules')}>
          {NAV.map((n, i) => (
            <Fragment key={n.to}>
            {groupAt.has(i) && <span className="railgrp">{groupAt.get(i)}</span>}
            <NavLink to={n.to} end={n.end} className={cls}>
              <span className="ri">{n.icon}</span>{n.label}
              {n.to === '/offene-punkte' && urgent > 0 && <span className="badge" aria-label={t('nav.issuesBadge', { n: urgent })}>{urgent}</span>}
            </NavLink>
            </Fragment>
          ))}
          <span className="spacer" />
          {me.role === 'admin' && (
            <NavLink to="/verwaltung" className={({ isActive }) => cls({ isActive: isActive || loc.pathname.startsWith('/verwaltung') })}>
              <span className="ri">{Icon.people}</span>{t('nav.admin')}
            </NavLink>
          )}
          <NavLink to="/hilfe" className={cls}><span className="ri">{Icon.help}</span>{t('nav.help')}</NavLink>
          <a className="railitem" href="/auth/logout"><span className="ri">{Icon.logout}</span>{t('app.logout')}</a>
        </nav>
        <main className="content">{children}</main>
      </div>
      {overlay === 'search' && <CommandPalette me={me} onClose={() => setOverlay(null)} />}
      {overlay === 'keys' && <ShortcutHelp onClose={() => setOverlay(null)} />}
      {overlay === 'tour' && <Tour onClose={() => setOverlay(null)} />}
      {overlay === 'photo' && <ProfilePhotoDialog me={me} onClose={() => setOverlay(null)} onDone={(msg) => { setOverlay(null); sayPhoto(msg); }} />}
      {photoToast}
    </div>
  );
}
